import {ChartDataPoint, ChartDataSeriesOptions} from "canvasjs";
import {DurationTuple} from "@shared/api/model/timestamp";
import moment, {Duration, Moment} from 'moment-timezone';
import {SearchMode, SearchUtils} from "@shared/util/general/search-utils";
import {PeriodBounds} from "@shared/model/period-bounds.interface";
import {ALERT_FAULT, ALERT_NON_FAULT, BinValueFloor, ChartSeriesDataType, SeriesKey} from "@app/layout/charts/mini-chart/mini-chart.interface";
import {ScheduleUtil, WorkHoursWeekdayBoundsConfig, WorkingHourSecondsToMidnightBounds} from "@app/layout/charts/schedule-util";
import {PeriodUtilService} from "@shared/services/util/period-util.service";
import {ChartsUtil, IsoWeekday} from "@app/layout/charts/charts-util";
import {AceLoggerService} from "@logging/ace-logger.service";
import {AceLogger} from "@logging/ace-logger";
import {LabelChartDataPoint} from "@shared/components/canvas-chart/canvas-chart.component";
import {MessageTranslateService} from "@shared/services";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {StringUtils} from "@shared/util/general/string-utils";
import {AppConstants} from "@shared/util/app-constants";


interface CSVExportData<T> {
  keys: (keyof T)[];
  headers: string[];
  data: T[];
  bounds?: {start: number, end: number};
}

type PivotCsvRow = Record<string, any>;

const workingHoursColumnName = 'workingHours' as const;

interface FlatCsvRow {
  name: string;
  id: number | string;
  timestamp?: string;
  data: string;
  unit: string;
  includedEstimate?: string;
  [workingHoursColumnName]?: WorkingHours;
}

export type CSVPivotData = CSVExportData<PivotCsvRow>;
export type CSVFlatData = CSVExportData<FlatCsvRow>;


export interface MiniChartDataSeriesOptions extends ChartDataSeriesOptions {
  seriesData: ChartSeriesDataType;
}


export interface CSVPivotConfig {
  period?: DurationTuple;
  series: MiniChartDataSeriesOptions[];
  previousSeries: Map<string, MiniChartDataSeriesOptions>;
}


export interface ViewportBounds {
  viewportMinimum: number;
  viewportMaximum: number;
}


export interface ValueBasedPivotConfig {
  series: MiniChartDataSeriesOptions[];
  xAxisSampleSizeByBinFloor: ReadonlyMap<BinValueFloor, number>;
  sampleSizeBySeriesKeyByBinFloor: ReadonlyMap<BinValueFloor, ReadonlyMap<SeriesKey, number>>;
  xAxisEquipmentName: string;
  xAxisMetadataName: string;
  xAxisUnitName: string;
  viewportBounds: ViewportBounds | null;
}


export interface ExtraPivotColumnConfig {
  getKey: (valueKey: string) => string;
  getHeaderString: (valueHeader: string) => string;
  getValue: (dataPoint: ChartDataPoint) => string;
}


enum WorkingHours {
  IN_WORKING_HOURS = 1,
  NOT_IN_WORKING_HOURS = 0
}
namespace WorkingHours {
  export function encode(inWorkingHours: boolean): WorkingHours {
    return inWorkingHours ? WorkingHours.IN_WORKING_HOURS : WorkingHours.NOT_IN_WORKING_HOURS;
  }
}


export class ExportDataBuilder {
  static readonly CSV_TIMESTAMP_FORMAT = AppConstants.TIME_FORMAT_MINUTE_PRECISION;

  private readonly logger: AceLogger;
  private readonly DEFAULT_DATA_INTERVAL_LOWER = 12;
  private readonly DEFAULT_DATA_INTERVAL_UPPER = 18;
  private readonly DEFAULT_DATA_INTERVAL: DurationTuple = {amount: 15, unit: 'minutes'};
  private readonly TS_COLUMN_NAME = 'ts';
  private readonly TIMESTAMP_TRANSLATED = this.translateService.instant('Timestamp');
  private readonly WORKING_HOURS_TRANSLATED = this.translateService.instant('Working Hours');

  constructor(private effectiveTimezone: string,
              private periodUtilService: PeriodUtilService,
              private translateService: MessageTranslateService,
              private comparePreviousOffset: Duration,
              logService: AceLoggerService) {
    this.logger = logService.create(ExportDataBuilder.name);
  }

  resolvePivotConfig(dataSeries: MiniChartDataSeriesOptions[]): CSVPivotConfig | null {
    let averageIntervalSum = 0;
    let totalSamplePointCount = 0;
    const mainSeries: MiniChartDataSeriesOptions[] = [];
    const previousSeries = new Map<string, MiniChartDataSeriesOptions>();
    let nonLabeledSeriesFound = false;
    dataSeries.forEach((series: MiniChartDataSeriesOptions) => {
      const dataPoints = series.dataPoints;
      const labeled = dataPoints[0].label != null;
      // do not sample task alert data as it's not interval based
      if (!labeled && !ChartSeriesDataType.isAlertHistory(series.seriesData)) {
        nonLabeledSeriesFound = true;
        if (dataPoints.length > 1) { // only calculate avg interval for series that have more than 1 data points
          // estimate over only the first 10 data points
          const sampleCount = Math.min(10, dataPoints.length);
          const startMoment = moment(dataPoints[0].x);
          const endMoment = moment(dataPoints[sampleCount - 1].x);
          averageIntervalSum += endMoment.diff(startMoment, 'minutes');
          totalSamplePointCount += sampleCount;
        }
      }
      let seriesId = series.seriesData.id.toString();
      if (ChartsUtil.isPreviousSeriesId(seriesId)) {
        previousSeries.set(seriesId, series);
      } else {
        mainSeries.push(series);
      }
    });

    if (!mainSeries?.length && !previousSeries.size) {
      return null;
    }

    if (nonLabeledSeriesFound) {
      const averageInterval = averageIntervalSum / totalSamplePointCount;
      this.logger.debug('Average interval between points ' + averageInterval + ' minutes');
      const useDefaultDataInterval = this.DEFAULT_DATA_INTERVAL_LOWER <= averageInterval && averageInterval <= this.DEFAULT_DATA_INTERVAL_UPPER;
      return {
        series: mainSeries,
        previousSeries,
        ...useDefaultDataInterval && {period: this.DEFAULT_DATA_INTERVAL}
      };
    } else {
      return {period: null, series: mainSeries, previousSeries};
    }
  }

  buildPivotData(pivotConfig: CSVPivotConfig,
                 axisViewportBoundsByAxisIndex: Map<number, ViewportBounds>,
                 workHourBoundsByWeekday: Map<IsoWeekday, WorkHoursWeekdayBoundsConfig> | null): CSVPivotData {
    // Create a copy as entries will be deleted from this Map
    const previousSeriesById = new Map(pivotConfig.previousSeries);
    const {series: availableSeries, period} = pivotConfig;
    const tsKeyName = this.TS_COLUMN_NAME;
    const csvHeaders = [];
    const csvKeys = [];
    if (availableSeries?.length) {
      csvKeys.push(tsKeyName);
      csvHeaders.push(this.getTimestampHeader());
      if (workHourBoundsByWeekday) {
        csvKeys.push(workingHoursColumnName);
        csvHeaders.push(this.WORKING_HOURS_TRANSLATED);
      }
    }

    let {min: actualMin, max: actualMax} = this.findBoundsInSeries(availableSeries, axisViewportBoundsByAxisIndex);
    let {min: prevActualMin, max: prevActualMax} = this.findBoundsInSeries(previousSeriesById.values(), axisViewportBoundsByAxisIndex);

    if (prevActualMin != null) {
      if (!this.comparePreviousOffset) {
        throw new Error('comparePreviousOffset must be set when graphing previous period data');
      }

      const minMoment = moment(actualMin);
      const prevMinMoment = moment(prevActualMin);
      actualMin = moment.min(minMoment, prevMinMoment.clone().add(this.comparePreviousOffset)).valueOf();
      prevActualMin = period
        ? this.periodUtilService.roundDateTime(moment.min(prevMinMoment, minMoment.clone().subtract(this.comparePreviousOffset)), period).valueOf()
        : prevActualMin;
      prevActualMax = period
        ? this.periodUtilService.roundDateTime(moment(prevActualMax), period).valueOf()
        : prevActualMax;
    }

    actualMin = period ? this.periodUtilService.roundDateTime(moment(actualMin), period).valueOf() : actualMin;
    actualMax = period ? this.periodUtilService.roundDateTime(moment(actualMax), period).valueOf() : actualMax;

    const tsPrevKeyName = tsKeyName + '_prev';
    const csvPreviousKeys: string[] = [];
    const csvPreviousHeaders: string[] = [];
    if (previousSeriesById?.size) {
      csvPreviousHeaders.push(this.translateService.instant('Comparison Timestamp') + ' (' + this.effectiveTimezone + ')');
      csvPreviousKeys.push(tsPrevKeyName);
    }

    const csvData = [];
    const workHourSecondsToMidnightBoundsByWeekday = this.getWorkHourSecondsToMidnightBoundsByWeekday(workHourBoundsByWeekday);
    availableSeries?.forEach(series => {
      const seriesData = series.seriesData;
      this.buildSeriesPivotData(csvData, csvHeaders, csvKeys, [], series, tsKeyName, period,
        axisViewportBoundsByAxisIndex.get(series.axisXIndex || 0), actualMin, workHourSecondsToMidnightBoundsByWeekday);
      const previousSeriesId = ChartsUtil.createPreviousSeriesId(seriesData.id.toString());
      const previousSeries = previousSeriesById.get(previousSeriesId);
      if (previousSeries) {
        this.buildSeriesPivotData(csvData, csvPreviousHeaders, csvPreviousKeys, csvKeys, previousSeries, tsPrevKeyName, period,
          axisViewportBoundsByAxisIndex.get(previousSeries.axisXIndex || 0), prevActualMin, workHourSecondsToMidnightBoundsByWeekday);
        previousSeriesById.delete(previousSeriesId);
      }
    });

    // Add any previous series for which the main series is not visible
    previousSeriesById.forEach(previousSeries => {
      this.buildSeriesPivotData(csvData, csvPreviousHeaders, csvPreviousKeys, csvKeys, previousSeries, tsPrevKeyName, period,
        axisViewportBoundsByAxisIndex.get(previousSeries.axisXIndex || 0), prevActualMin, workHourSecondsToMidnightBoundsByWeekday);
    });

    return {
      keys: csvPreviousKeys?.length ? csvKeys.concat(csvPreviousKeys) : csvKeys,
      headers: csvPreviousHeaders?.length ? csvHeaders.concat(csvPreviousHeaders) : csvHeaders,
      data: csvData,
      bounds: {start: actualMin != null ? actualMin : prevActualMin, end: actualMax != null ? actualMax : prevActualMax}
    };
  }

  buildValueBasedPivotData(valueBasedPivotConfig: ValueBasedPivotConfig): CSVPivotData {
    const keys = {xAxisBin: 'xAxisBin', xAxisCount: 'xAxisCount', sampleSizePrefix: 'sampleSize-'};
    const csvKeys: string[] = [keys.xAxisBin, keys.xAxisCount];
    const xAxisColumnLabel = ChartsUtil.buildEquipmentMetadataLabel(valueBasedPivotConfig.xAxisEquipmentName,
      valueBasedPivotConfig.xAxisMetadataName);
    const csvHeaders: string[] = [
      xAxisColumnLabel + ', ' + valueBasedPivotConfig.xAxisUnitName,
      ChartsUtil.buildCountLabel(xAxisColumnLabel)
    ];
    const seriesColumns: {key: SeriesKey, countKey: string | null, valueByBinFloor: Map<BinValueFloor, number>}[] = [];
    valueBasedPivotConfig.series.forEach(series => {
      const valueByBinFloor = CollectionUtils.indexByFn(series.dataPoints, dataPoint => <BinValueFloor>dataPoint.x, dataPoint => dataPoint.y);
      const key = ChartSeriesDataType.buildKey(series.seriesData);
      csvKeys.push(key);
      csvHeaders.push(series.legendText);
      const countKey = ChartSeriesDataType.isPointHistory(series.seriesData) ? (keys.sampleSizePrefix + key) : null;
      if (countKey != null) {
        csvKeys.push(countKey);
        csvHeaders.push(ChartsUtil.buildCountLabel(series.legendText));
      }
      seriesColumns.push({key, countKey, valueByBinFloor});
    });

    // Include only points the user has zoomed into if they are zoomed in. Otherwise, include all points.
    const viewportMinimum = valueBasedPivotConfig.viewportBounds?.viewportMinimum ?? Number.NEGATIVE_INFINITY;
    const viewportMaximum = valueBasedPivotConfig.viewportBounds?.viewportMaximum ?? Number.POSITIVE_INFINITY;

    const csvData: PivotCsvRow[] = [];
    Array.from(valueBasedPivotConfig.xAxisSampleSizeByBinFloor.keys()).sort((binFloorA, binFloorB) => binFloorA - binFloorB).forEach(binFloor => {
      if (binFloor >= viewportMinimum && binFloor <= viewportMaximum) {
        const csvRow: PivotCsvRow = {
          [keys.xAxisBin]: binFloor,
          [keys.xAxisCount]: valueBasedPivotConfig.xAxisSampleSizeByBinFloor.get(binFloor)
        };
        // Add each series' value and sample size for the binFloor (the row) as column cells
        const sampleSizeBySeriesKey = valueBasedPivotConfig.sampleSizeBySeriesKeyByBinFloor.get(binFloor);
        seriesColumns.forEach(seriesColumn => {
          csvRow[seriesColumn.key] = seriesColumn.valueByBinFloor.get(binFloor) ?? '';
          if (seriesColumn.countKey != null) {
            csvRow[seriesColumn.countKey] = sampleSizeBySeriesKey?.get(seriesColumn.key) ?? '';
          }
        });
        csvData.push(csvRow);
      }
    });

    return {keys: csvKeys, headers: csvHeaders, data: csvData};
  }

  buildFlatData(chartDataSeries: MiniChartDataSeriesOptions[],
                unitsBySeriesId: Map<number | string, string>,
                axisXViewportBoundsByAxisIndex: Map<number, ViewportBounds>,
                workHourBoundsByWeekday: Map<IsoWeekday, WorkHoursWeekdayBoundsConfig> | null): CSVFlatData {
    let isMeterStream: boolean = null;
    const seriesById = new Map<string, MiniChartDataSeriesOptions>();
    const visibleMainSeriesIds = new Set<string>();
    const visiblePreviousSeriesIds = new Set<string>();
    chartDataSeries.forEach(series => {
      const seriesId = series.seriesData.id.toString();
      seriesById.set(seriesId, series);
      const s = ChartsUtil.isPreviousSeriesId(seriesId) ? visiblePreviousSeriesIds : visibleMainSeriesIds;
      if (!s.has(seriesId)) {
        s.add(seriesId);
      } else {
        throw new Error('Duplicate series id "' + seriesId + '"');
      }
    });

    const csvData: FlatCsvRow[] = [];
    const workHourSecondsToMidnightBoundsByWeekday = this.getWorkHourSecondsToMidnightBoundsByWeekday(workHourBoundsByWeekday);
    let overallMin: number;
    let overallMax: number;
    visibleMainSeriesIds.forEach(seriesId => {
      const series = seriesById.get(seriesId);
      const unit = unitsBySeriesId.get(seriesId);
      isMeterStream = ChartSeriesDataType.isMeterHistory(series.seriesData);
      const mainSeriesIndexes = this.getCSVExportIndexes(series, axisXViewportBoundsByAxisIndex.get(series.axisXIndex || 0));
      const {data, min, max} = this.getDataFromSeries(
        series, series.seriesData.id, series.legendText, unit, mainSeriesIndexes.startIndex, mainSeriesIndexes.endIndex, workHourSecondsToMidnightBoundsByWeekday);
      if (min != null) {
        overallMin = overallMin == null ? min : Math.min(overallMin, min);
      }
      if (max != null) {
        overallMax = overallMax == null ? max : Math.max(overallMax, max);
      }

      const previousSeriesId = ChartsUtil.createPreviousSeriesId(series.seriesData.id.toString());
      const previousSeries = visiblePreviousSeriesIds.has(previousSeriesId) && seriesById.get(previousSeriesId);
      if (previousSeries) {
        if (previousSeries.dataPoints?.length) {
          const prevSeriesIndexes = this.getCSVExportIndexes(previousSeries, axisXViewportBoundsByAxisIndex.get(previousSeries.axisXIndex || 0));
          const firstMainPointXVal = this.getX(series.dataPoints[mainSeriesIndexes.startIndex]);
          const lastPrevPointXVal = this.getX(previousSeries.dataPoints[prevSeriesIndexes.endIndex - 1]);

          const firstMainAsNumber = firstMainPointXVal instanceof Date ? firstMainPointXVal.getTime() : firstMainPointXVal;
          const lastPrevAsNumber = lastPrevPointXVal instanceof Date ? lastPrevPointXVal.getTime() : lastPrevPointXVal;

          let endIndex = prevSeriesIndexes.endIndex;
          if (firstMainAsNumber < lastPrevAsNumber) {
            endIndex = SearchUtils.sortedIndex(previousSeries.dataPoints, p => {
              const xValue = this.getX(p);
              return firstMainAsNumber - (xValue instanceof Date ? xValue.getTime() : xValue);
            }, SearchMode.FIND_FIRST);
          }
          const {data, min, max} = this.getDataFromSeries(
            previousSeries, series.seriesData.id, series.legendText, unit, prevSeriesIndexes.startIndex, endIndex, workHourSecondsToMidnightBoundsByWeekday);
          if (min != null) {
            overallMin = overallMin == null ? min : Math.min(overallMin, min);
          }
          if (max != null) {
            overallMax = overallMax == null ? max : Math.max(overallMax, max);
          }
          csvData.push(...data);
        }
        visiblePreviousSeriesIds.delete(previousSeriesId);
      }
      // Ensure the main series data is added after the previous series data
      csvData.push(...data);
    });

    // Export any previous series that have non-visible main series
    visiblePreviousSeriesIds.forEach(previousSeriesId => {
      const previousSeries = seriesById.get(previousSeriesId);
      const mainSeriesEntry = seriesById.get(ChartsUtil.createMainSeriesId(previousSeriesId));
      if (isMeterStream == null) {
        isMeterStream = ChartSeriesDataType.isMeterHistory(previousSeries.seriesData);
      }
      const unit = unitsBySeriesId.get(previousSeriesId);

      const indexes = this.getCSVExportIndexes(previousSeries, axisXViewportBoundsByAxisIndex.get(previousSeries.axisXIndex || 0));
      const {data, min, max} = this.getDataFromSeries(previousSeries, mainSeriesEntry?.seriesData?.id || previousSeries.seriesData.id,
        mainSeriesEntry?.legendText || previousSeries.legendText, unit, indexes.startIndex, indexes.endIndex, workHourSecondsToMidnightBoundsByWeekday);
      if (min != null) {
        overallMin = overallMin == null ? min : Math.min(overallMin, min);
      }
      if (max != null) {
        overallMax = overallMax == null ? max : Math.max(overallMax, max);
      }
      csvData.push(...data);
    });

    const haveBounds = !!axisXViewportBoundsByAxisIndex.get(0)?.viewportMinimum;
    const flatCsvData: CSVFlatData = {
      keys: [
        'name',
        'id',
        'timestamp',
        ...(workHourBoundsByWeekday ? [workingHoursColumnName] : []),
        'data',
        'unit'
      ],
      headers: [
        isMeterStream
          ? ('"' + this.translateService.instant('Meter Stream') + '"')
          : ('"' + [
              this.translateService.instant('Equipment Name'),
              this.translateService.instant('Favourite Name'),
              this.translateService.instant('Level'),
              this.translateService.instant('Zone')
            ].join(', ') + '"'
          ),
        this.translateService.instant('ID'),
        this.getTimestampHeader(),
        ...(workHourBoundsByWeekday ? [this.WORKING_HOURS_TRANSLATED] : []),
        this.translateService.instant('Value'),
        this.translateService.instant('Unit'),
      ],
      data: csvData,
      ...haveBounds && {bounds: {start: overallMin, end: overallMax}}
    };
    /** For meters, add column for estimates. `hasEstimates` is to hide from non Licensor because their datapoints will not have estimates.
     * This will hide it when there are no estimates as well. */
    if (isMeterStream && flatCsvData.data.some(datum => datum.includedEstimate)) {
      flatCsvData.keys.push('includedEstimate');
      flatCsvData.headers.push('Included Estimate');
    }
    return flatCsvData;
  }

  prepareSeriesDataPointsForExport(seriesData: ChartSeriesDataType, dataPoints: ChartDataPoint[]): ChartDataPoint[] {
    if (ChartSeriesDataType.isAlertHistory(seriesData)) {
      const alertBoundaryPoints = [];
      let alertOpen = false;
      dataPoints.forEach(dataPoint => {
        if (dataPoint.y === ChartsUtil.ALERT_VALUES.ALERT) {
          if (alertOpen) {
            alertOpen = false;
            alertBoundaryPoints.push({
              ...dataPoint,
              // ChartsUtil.ALERT_VALUES.NO_ALERT is null, doesn't look very informative as 0 in the export data, so here we use 0 instead.
              y: 0
            });
          } else {
            alertOpen = true;
            alertBoundaryPoints.push(dataPoint);
          }
        }
      });
      return alertBoundaryPoints;
    } else {
      return dataPoints;
    }
  }

  private buildSeriesPivotData(csvData: PivotCsvRow[],
                               csvHeaders: string[],
                               csvKeys: string[],
                               otherCsvKeys: string[],
                               series: MiniChartDataSeriesOptions,
                               tsKeyName: string,
                               period: DurationTuple,
                               viewportBounds: ViewportBounds,
                               minimum: number,
                               workHourSecondsToMidnightBoundsByWeekday: Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> | null): void {
    const seriesData = series.seriesData;
    let padFn: (ts: Moment) => any;
    let valueFn: (dataPoint: ChartDataPoint, ts: Moment) => any;
    let extraColumnConfig: ExtraPivotColumnConfig = null;
    if (ChartSeriesDataType.isAlertHistory(seriesData)) {
      const alertBounds = this.getAlertPeriodBounds(series.dataPoints);
      padFn = (ts: Moment) => this.inAlert(ts, alertBounds, period);
      valueFn = (_dataPoint: ChartDataPoint, ts: Moment) => this.inAlert(ts, alertBounds, period);
    } else {
      padFn = (_ts: Moment) => '';
      valueFn = (dataPoint: ChartDataPoint, _ts: Moment) => dataPoint.y ?? '';
      // Does not show estimates if there are none. Hides it for non licenser users as well because estimate property is not filled in unless user is licenser.
      if (ChartSeriesDataType.isMeterHistory(seriesData) && series.dataPoints.some(point => this.getAbsoluteEstimate(point))) {
        extraColumnConfig = {
          getKey: valueKey => 'includedEstimate' + '-' + valueKey,
          getHeaderString: valueHeader => this.translateService.instant({messageKey: 'Included Estimate'}) + ' (' + valueHeader + ')',
          getValue: point => this.getAbsoluteEstimate(point)?.toString() ?? ''
        };
      }
    }
    return this.buildDefaultSeriesPivotData(csvData, csvHeaders, csvKeys, otherCsvKeys, series, tsKeyName, period,
      viewportBounds, minimum, padFn, valueFn, extraColumnConfig, workHourSecondsToMidnightBoundsByWeekday);
  }

  private getCSVExportIndexes(series: MiniChartDataSeriesOptions, viewportBounds: ViewportBounds): {startIndex: number, endIndex: number} {
    const viewportMinimum = viewportBounds?.viewportMinimum;
    const viewportMaximum = viewportBounds?.viewportMaximum;
    const labeledPoints = series.dataPoints[0].label != null;

    const startIndex = viewportMinimum == null
      ? 0
      : (labeledPoints
        ? Math.max(0, Math.ceil(viewportMinimum))
        : SearchUtils.sortedIndex(series.dataPoints, p => {
          return viewportMinimum - (p.x instanceof Date ? p.x.getTime() : p.x);
        }, SearchMode.FIND_FIRST));

    const endIndex = (viewportMaximum == null)
      ? series.dataPoints.length
      : (labeledPoints
        ? Math.min(series.dataPoints.length, Math.floor(viewportMaximum) + 1)
        : SearchUtils.sortedIndex(series.dataPoints, p => {
          return viewportMaximum - (p.x instanceof Date ? p.x.getTime() : p.x);
        }, SearchMode.FIND_LAST));

    return {startIndex, endIndex};
  }

  private getDataFromSeries(series: MiniChartDataSeriesOptions,
                            seriesId: number | string,
                            seriesExportName: string,
                            unit: string,
                            startIndex: number,
                            endIndex: number,
                            workHourSecondsToMidnightBoundsByWeekday: Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> | null): {data: FlatCsvRow[], min: number, max: number} {
    const csvData: FlatCsvRow[] = [];
    if (startIndex < endIndex) {
      const isMeterStream = ChartSeriesDataType.isMeterHistory(series.seriesData);
      const min = this.getX(series.dataPoints[startIndex]);
      const max = this.getX(series.dataPoints[endIndex - 1]);
      for (let i = startIndex; i < endIndex; i++) {
        const point = series.dataPoints[i];
        const xValue = this.getX(point);
        if (xValue != null) {
          const ts = moment.tz(xValue, this.effectiveTimezone);
          csvData.push({
            name: seriesExportName,
            id: seriesId,
            timestamp: ts.format(ExportDataBuilder.CSV_TIMESTAMP_FORMAT),
            data: point.y != null ? point.y.toString() : '',
            unit,
            includedEstimate: (isMeterStream && this.getAbsoluteEstimate(point)?.toString()) || '',
            ...workHourSecondsToMidnightBoundsByWeekday && {
              workingHours: this.inWorkingHours(ts, workHourSecondsToMidnightBoundsByWeekday)
            }
          });
        }
      }
      return {data: csvData, min: (min instanceof Date ? min.getTime() : min), max: (max instanceof Date ? max.getTime() : max)};
    } else {
      return {data: csvData, min: null, max: null}
    }
  }

  private findBoundsInSeries(seriesCollection: Iterable<MiniChartDataSeriesOptions>,
                             axisViewportBoundsByAxisIndex: Map<number, ViewportBounds>): {min: number, max: number} {
    let min = null;
    let max = null;
    for (const series of (seriesCollection || [])) {
      const exportIndexes = this.getCSVExportIndexes(series, axisViewportBoundsByAxisIndex.get(series.axisXIndex || 0));
      const minValue = this.getX(series.dataPoints[exportIndexes.startIndex]);
      const maxValue = this.getX(series.dataPoints[exportIndexes.endIndex - 1]);
      min = min == null ? minValue : Math.min(min, minValue instanceof Date ? minValue.getTime() : minValue);
      max = max == null ? maxValue : Math.max(max, maxValue instanceof Date ? maxValue.getTime() : maxValue);
    }
    return {min, max};
  }

  private getX(p: LabelChartDataPoint): number | Date {
    return p.label != null ? p.xValue : p.x;
  }

  private getAbsoluteEstimate(p: LabelChartDataPoint): number | null {
    return p.absoluteEstimate ?? null;
  }

  private getAlertPeriodBounds(dataPoints: ChartDataPoint[]): PeriodBounds[] {
    const periodBounds: PeriodBounds[] = [];
    for (let i = 0; i < dataPoints.length; i += 2) {
      periodBounds.push({
        startTs: moment.tz(dataPoints[i].x, this.effectiveTimezone),
        ...(dataPoints.length > (i + 1)) && {
          endTs: moment.tz(dataPoints[i + 1].x, this.effectiveTimezone)
        }
      })
    }
    return periodBounds;
  }

  private inAlert(ts: Moment, alertBounds: PeriodBounds[], period: DurationTuple): string {
    return alertBounds?.some(({startTs, endTs}) => {
      return !ts.isBefore(startTs, period.unit) && (!endTs || ts.isBefore(endTs, period.unit));
    }) ? ALERT_FAULT : ALERT_NON_FAULT;
  }

  private inWorkingHours(ts: Moment, workHourSecondsToMidnightBoundsByWeekday: Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> | null): WorkingHours {
    return WorkingHours.encode(ScheduleUtil.isTimestampWithinWorkingHours(ts, workHourSecondsToMidnightBoundsByWeekday));
  }

  private buildDefaultSeriesPivotData(csvData: PivotCsvRow[],
                                      csvHeaders: string[],
                                      csvKeys: string[],
                                      otherCsvKeys: string[],
                                      series: MiniChartDataSeriesOptions,
                                      tsKeyName: string,
                                      period: DurationTuple,
                                      viewportBounds: ViewportBounds,
                                      minimum: number,
                                      padFn: (ts: Moment) => any,
                                      valueFn: (dataPoint: ChartDataPoint, ts: Moment) => any,
                                      extraColumnConfig: ExtraPivotColumnConfig | null,
                                      workHourSecondsToMidnightBoundsByWeekday: Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> | null): void {
    let csvDataIndex = 0;
    let nextTs = minimum != null ? moment(minimum) : null;

    const getNextCSVRow = (index: number, tsKeyName: string, tsKeyValue: string): PivotCsvRow | null => {
      if (index > csvData.length) {
        throw new Error('Index ' + index + ' too large for array length ' + csvData.length);
      } else if (index === csvData.length) {
        csvData.push({});
      }

      const csvRow = csvData[index];
      if (tsKeyName in csvRow) {
        const csvRowTs = csvRow[tsKeyName];
        if (csvRowTs !== tsKeyValue) {
          this.logger.error('Point labels do not match. Expected ' + csvRow[tsKeyName] + ' but found ' + tsKeyValue, 'series: ' + series.name);
          return null;
        }
      } else {
        const prevTsValue = index > 0 ? csvData[index - 1][tsKeyName] : null;
        if (prevTsValue && prevTsValue === tsKeyValue) {
          // if there's a data clash (due to same timestamp appearing multiple times in a row), return null so the data point is ignored
          this.logger.error('Point labels clashed at ' + tsKeyValue, 'series: ' + series.name);
          return null;
        }
        csvRow[tsKeyName] = tsKeyValue;
        // If the other series do not have entries, pad with empty spaces so that 'null' is not printed in the CSV file
        csvKeys.concat(otherCsvKeys || []).forEach(headerKey => {
          if (!(headerKey in csvRow)) {
            csvRow[headerKey] = '';
          }
        });
      }

      if (workHourSecondsToMidnightBoundsByWeekday && StringUtils.isEmpty(csvRow[workingHoursColumnName])) {
        csvRow[workingHoursColumnName] = this.inWorkingHours(nextTs, workHourSecondsToMidnightBoundsByWeekday);
      }

      return csvRow;
    };

    const incrementCSVIndexes = function () {
      csvDataIndex++;
      if (nextTs && period) {
        nextTs.add(period.amount, period.unit);
      }
    };

    const key = ChartSeriesDataType.buildKey(series.seriesData);
    const extraKey = extraColumnConfig?.getKey(key);
    const mainIndexes = this.getCSVExportIndexes(series, viewportBounds);

    for (let i = mainIndexes.startIndex; i < mainIndexes.endIndex; i++) {
      const p = series.dataPoints[i];

      let xValue: string;
      if (p.label != null) {
        xValue = p.label;
      } else {
        if (nextTs == null) {
          throw new Error('Expected next timestamp to be defined');
        }

        const val = this.periodUtilService.roundDateTime(moment(p.x), period).tz(this.effectiveTimezone);
        xValue = val.format(ExportDataBuilder.CSV_TIMESTAMP_FORMAT);
        while (val.isAfter(nextTs, period.unit)) {
          const csvRow = getNextCSVRow(csvDataIndex, tsKeyName, nextTs.clone().tz(this.effectiveTimezone).format(ExportDataBuilder.CSV_TIMESTAMP_FORMAT));
          if (csvRow) {
            csvRow[key] = padFn(nextTs);
            incrementCSVIndexes();
          } else {
            throw new Error('Cannot locate the correct csv row with ts: ' + nextTs.toISOString());
          }
        }
      }

      const csvRow = getNextCSVRow(csvDataIndex, tsKeyName, xValue);
      // if csvRow cannot be found, discard current data point, it's likely a duplicated data point, do not advance in csv data or nextTs
      if (csvRow) {
        csvRow[key] = valueFn(p, nextTs);
        if (extraColumnConfig) {
          csvRow[extraKey] = extraColumnConfig.getValue(p)
        }
        incrementCSVIndexes();
      }
    }

    // If the series has less data than other series, pad with empty spaces so that 'null' is not printed in the CSV file
    for (let i = csvDataIndex; i < csvData.length; i++) {
      csvData[i][key] = '';
    }

    csvHeaders.push(series.legendText);
    csvKeys.push(key);
    if (extraColumnConfig) {
      csvHeaders.push(extraColumnConfig.getHeaderString(series.legendText));
      csvKeys.push(extraKey);
    }
  }

  private getWorkHourSecondsToMidnightBoundsByWeekday(
    workHourBoundsByWeekday: Map<IsoWeekday, WorkHoursWeekdayBoundsConfig> | null
  ): Map<IsoWeekday, WorkingHourSecondsToMidnightBounds> | null {
    return workHourBoundsByWeekday?.size
      ? ScheduleUtil.buildWorkingHoursSecondsToMidnightBoundsByWeekday(workHourBoundsByWeekday)
      : null;
  }

  private getTimestampHeader(): string {
    return this.TIMESTAMP_TRANSLATED + ' (' + this.effectiveTimezone + ')';
  }
}
